<template>
  <div
    v-if="show && protest"
    class="customer-popup-overlay"
    @click.self="$emit('close')"
  >
    <div class="customer-popup-content protest-popup-content">
      <!-- Header -->
      <div class="customer-popup-header">
        <div>
          <h3 class="customer-popup-title">Protest Events</h3>
        </div>
        <button @click="$emit('close')" class="customer-popup-close" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      
      <!-- Body -->
      <div class="customer-popup-body">
        <!-- Empty State -->
        <div v-if="filteredEvents.length === 0" class="protest-empty-state">
          <svg class="protest-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <p class="protest-empty-title">No data found</p>
          <p class="protest-empty-subtitle">No protest events match the selected filters</p>
        </div>
        
        <!-- Events Table -->
        <div v-else class="protest-events-table-container">
          <table class="protest-events-table">
            <thead>
              <tr>
                <th style="color: var(--foreground) !important;">Date</th>
                <th style="color: var(--foreground) !important;">Type</th>
                <th style="color: var(--foreground) !important;">Claims</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(event, index) in filteredEvents" :key="index">
                <td class="protest-date">{{ formatDate(event.date) }}</td>
                <td class="protest-type">{{ event.event_type || 'N/A' }}</td>
                <td class="protest-claims">{{ event.claims_summary || 'N/A' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="customer-popup-footer">
        <button @click="$emit('close')" class="customer-popup-button">
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProtestPopup',
  
  props: {
    protest: {
      type: Object,
      default: null
    },
    show: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => ({
        dateRange: null,
        searchText: '',
        selectedFactors: []
      })
    }
  },
  
  computed: {
    events() {
      return this.protest?.events || [];
    },
    
    eventCount() {
      return this.events.length;
    },
    
    filteredEvents() {
      let events = [...this.events];
      
      // Apply date range filter
      if (this.filters.dateRange && this.filters.dateRange.startDate && this.filters.dateRange.endDate) {
        events = this.filterByDateRange(events);
      }
      
      // Apply search text filter
      if (this.filters.searchText && this.filters.searchText.trim()) {
        events = this.filterBySearchText(events);
      }
      
      // Apply factors filter
      if (this.filters.selectedFactors && this.filters.selectedFactors.length > 0) {
        events = this.filterByFactors(events);
      }
      
      return events;
    }
  },

  mounted() {
    document.addEventListener('keydown', this.handleEscKey);
  },
  
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscKey);
  },
  
  methods: {
    handleEscKey(event) {
      if (event.key === 'Escape' && this.show) {
        this.$emit('close');
      }
    },
    
    formatDate(dateStr) {
      if (!dateStr || dateStr === 'N/A') return 'N/A';
      
      try {
        // Parse DD-MM-YYYY format and convert to MM-DD-YYYY display
        const dateParts = dateStr.split('-');
        if (dateParts.length === 3) {
          const day = dateParts[0];
          const month = dateParts[1];
          const year = dateParts[2];
          // Return in MM-DD-YYYY format
          return `${month}-${day}-${year}`;
        }
        return dateStr;
      } catch (e) {
        console.warn('Error formatting date:', e);
        return dateStr;
      }
    },
    
    parseEventDate(dateStr) {
      try {
        // Parse DD-MM-YYYY format to Date object
        const [day, month, year] = dateStr.split('-').map(Number);
        if (!day || !month || !year) return null;
        return new Date(year, month - 1, day);
      } catch (e) {
        console.warn('Error parsing event date:', e);
        return null;
      }
    },
    
    parseFilterDate(dateStr) {
      try {
        // Parse MM-DD-YYYY format to Date object
        const [month, day, year] = dateStr.split('-').map(Number);
        if (!day || !month || !year) return null;
        return new Date(year, month - 1, day);
      } catch (e) {
        console.warn('Error parsing filter date:', e);
        return null;
      }
    },
    
    filterByDateRange(events) {
      const startDate = this.parseFilterDate(this.filters.dateRange.startDate);
      const endDate = this.parseFilterDate(this.filters.dateRange.endDate);
      
      if (!startDate || !endDate) return events;
      
      endDate.setHours(23, 59, 59, 999); // Set to end of day
      
      return events.filter(event => {
        if (!event.date) return false;
        
        const eventDate = this.parseEventDate(event.date);
        if (!eventDate) return false;
        
        return eventDate >= startDate && eventDate <= endDate;
      });
    },
    
    filterBySearchText(events) {
      const searchText = this.filters.searchText.toLowerCase().trim();
      
      return events.filter(event => {
        const searchableText = `${event.date || ''} ${event.event_type || ''} ${event.claims_summary || ''}`.toLowerCase();
        return searchableText.includes(searchText);
      });
    },
    
    filterByFactors(events) {
      return events.filter(event => {
        const eventText = `${event.event_type || ''} ${event.claims_summary || ''}`.toLowerCase();
        
        return this.filters.selectedFactors.some(factor => {
          // Handle both capitalized and lowercase factors
          const keyword = factor.toLowerCase();
          return eventText.includes(keyword);
        });
      });
    }
  }
};
</script>
